/**
 * useTenantAwareEntityList — Entity Framework fan-out for the owner
 * multi-tenant "All" mode (Prompt 11E.5 / D9, built once here, consumed by
 * every EntityTable-backed list page).
 *
 * Single-tenant mode (the default, and every non-owner account): delegates
 * entirely to the caller's own list query — no behavior change, no extra
 * requests.
 *
 * "All" mode (`useTenant().isAllMode`): issues one request per tenant in
 * `availableTenantIds`, each with an explicit `X-Tenant-Id` header override
 * (see `@core/api/client`), and merges the results client-side, tagging each
 * row with `_tenantId` for the Tenant column. This is a frontend-only
 * concept — there is no server-side "all tenants" value; every individual
 * request still targets exactly one tenant, and the backend independently
 * validates each tenant against the caller's claims.
 *
 * Pagination is intentionally not merged across tenants in "All" mode (each
 * tenant query still uses the caller's page/sort/search/filter params, but
 * the combined view shows one page per tenant, concatenated) — the calling
 * page must not render pagination controls while `isAllMode` is true.
 */

import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiCall } from '@core/api/client';
import { useTenant } from '@core/tenant/TenantContext';
import type { EntityListResult } from './types';

export type TenantTagged<T> = T & { _tenantId?: string };

interface UseTenantAwareEntityListArgs {
  /** Query key for the single-tenant (default) query — must be unique per param set. */
  queryKey: unknown[];
  /** List API path, e.g. "/v1/students". */
  apiPath: string;
  /** Query-string params forwarded to every request (single-tenant and per-tenant fan-out). */
  params: Record<string, string | number | boolean | undefined | null>;
}

interface UseTenantAwareEntityListResult<T> {
  data: EntityListResult<TenantTagged<T>> | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  /** Convenience passthrough — the calling page should hide pagination when true. */
  isAllMode: boolean;
  /**
   * Call before navigating to a row's detail page.
   *
   * In "All" mode no default `X-Tenant-Id` is set, so a detail fetch would
   * target the caller's HOME tenant and 404 for any row belonging to another
   * one. Selecting the row's own tenant first both fixes the fetch and drops
   * the user out of read-only All mode into the school they just drilled
   * into. No-op outside All mode, or for rows with no `_tenantId`.
   */
  focusRowTenant: (row: TenantTagged<T>) => void;
}

export function useTenantAwareEntityList<T extends { id: string }>({
  queryKey,
  apiPath,
  params,
}: UseTenantAwareEntityListArgs): UseTenantAwareEntityListResult<T> {
  const { isAllMode, availableTenantIds, selectedTenantId, setSelectedTenantId } = useTenant();

  const singleQuery = useQuery({
    // The selected tenant is part of the cache key. Without it, switching
    // tenants served the previous tenant's rows from cache — and with
    // `placeholderData: keepPrevious` below, rendered them even while the
    // new request was still in flight. TenantContext also clears the whole
    // query cache on a switch; this key makes the two tenants' entries
    // distinct in the first place.
    queryKey: [...queryKey, 'single', selectedTenantId],
    queryFn: () => apiCall<EntityListResult<T>>('GET', apiPath, undefined, { query: params }),
    enabled: !isAllMode,
    placeholderData: (prev) => prev,
  });

  const fanOutQueries = useQueries({
    queries: isAllMode
      ? availableTenantIds.map((tenantId) => ({
          queryKey: [...queryKey, 'tenant', tenantId],
          queryFn: () =>
            apiCall<EntityListResult<T>>('GET', apiPath, undefined, {
              query: params,
              headers: { 'X-Tenant-Id': tenantId },
            }),
        }))
      : [],
  }) as UseQueryResult<EntityListResult<T>, Error>[];

  const focusRowTenant = (row: TenantTagged<T>): void => {
    if (!isAllMode) return;
    const rowTenant = row?._tenantId;
    if (rowTenant && availableTenantIds.includes(rowTenant)) {
      setSelectedTenantId(rowTenant);
    }
  };

  if (!isAllMode) {
    return {
      data: singleQuery.data as EntityListResult<TenantTagged<T>> | undefined,
      isLoading: singleQuery.isLoading,
      error: singleQuery.error,
      refetch: () => void singleQuery.refetch(),
      isAllMode: false,
      focusRowTenant,
    };
  }

  const isLoading = fanOutQueries.some((q) => q.isLoading);
  const error = fanOutQueries.find((q) => q.error)?.error ?? null;
  const items: TenantTagged<T>[] = fanOutQueries.flatMap((q, idx) =>
    (q.data?.items ?? []).map((item) => ({ ...item, _tenantId: availableTenantIds[idx] })),
  );
  const total = fanOutQueries.reduce((sum, q) => sum + (q.data?.total ?? 0), 0);

  return {
    data: { items, total, page: 1, page_size: items.length || 1, pages: 1 },
    isLoading,
    error,
    refetch: () => fanOutQueries.forEach((q) => void q.refetch()),
    isAllMode: true,
    focusRowTenant,
  };
}

